import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AuthService } from 'src/app/auth/auth.service';
import { from, Observable } from 'rxjs';
import { switchMap, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ProductFilters, StockLotProduct } from '../core/interfaces/product.interface';
import { StoreDTO } from '../core/interfaces/stores.interface';
import { AnalyticsEvent } from 'src/app/analytics/models/analytics-event';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { ProductItemCategoryId, TireBrandsResponse, TireProduct } from '../core/interfaces/products-types/tire-product.interface';
import { ProductCategoryEnum } from '../core/enums/product-category.enum';
import { MarketplaceTireProductsResponse } from '../pages/marketplace/tires/interfaces/tires.interface';

export interface BrandGroup {
  groupName: string;
  brands: string[];
}
@Injectable({
  providedIn: 'root',
})
export class ProductsService {

  url = environment.cafler_backoffice_back_url;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly analytics: AnalyticsService
  ) { }

  getGenericProducts(searchText: string, brands?: string[] | null, categoryId?: ProductCategoryEnum | null): Observable<ProductItemCategoryId[]> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          throw new Error('No token available');
        }

        let params = new HttpParams();

        if (searchText) {
          params = params.set('searchText', searchText);
        }
        if (brands && brands.length > 0) {
          brands.forEach(brand => {
            params = params.append('brand', brand);
          });
        }
        if (categoryId) {
          params = params.set('productCategoryId', Number(categoryId));
        }

        return this.http.get<ProductItemCategoryId[]>(`${this.url}/products`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: params
        })
      }),
    );
  }

  getStockLotProducts(): Observable<StockLotProduct[]> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          throw new Error('No token available');
        }

        return this.http.get<StockLotProduct[]>(
          `${this.url}/store-stock-lots/products`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      })
    );
  }


  getStores(centerId?: number | null): Observable<StoreDTO[]> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          throw new Error('No token available');
        }

        const params = centerId != null
          ? new HttpParams().set('centerId', String(centerId))
          : undefined;

        return this.http.get<StoreDTO[]>(`${this.url}/stores`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });
      })
    );
  }

  getProductBrands(): Observable<TireBrandsResponse> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          throw new Error('No token available');
        }
        return this.http.get<TireBrandsResponse>(`${this.url}/products/brands`, { headers: { Authorization: 'Bearer ' + token } });
      })
    );
  }

  getProductBrandsGrouped(): Observable<BrandGroup[]> {
    return this.getProductBrands().pipe(
      map((brands: TireBrandsResponse | null) => {
        if (!brands) {
          return [];
        }
        return Object.entries(brands.brands).map(([groupName, brandList]: [string, any]) => ({
          groupName,
          brands: Array.isArray(brandList) ? [...new Set(brandList)].sort() : []
        }));
      })
    );
  }




}